import { Component, OnInit, OnDestroy } from '@angular/core';
import { ObservableMedia, MediaChange } from '@angular/flex-layout';

import { combineLatest } from 'rxjs/observable/combineLatest';
import { first } from 'rxjs/operators/first';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { takeUntil } from 'rxjs/operators/takeUntil';

import { ApplicationService, CollegeInfo, SchoolInfo } from '../../services/application/application.service';
import { DialogService, ReportRunOptions } from '../../services/dialogs/dialogs.service';
import { DownloadService, DownloadFormat, HttpMethod } from '../../services/download/download.service';
import { ErrorService } from '../../services/error/error.service';
import { ReportsService, ReportInfo } from '../../services/reports/reports.service';
import { SplashService } from '../../services/splash/splash.service';
import { UserService, UserInfo } from '../../services/user/user.service';

@Component({
    selector: 'app-reports-component',
    styleUrls: ['reports.component.css'],
    templateUrl: 'reports.component.html',
})
export class ReportsComponent implements OnInit, OnDestroy {
    private _subrelease: Subject<void> = new Subject<void>();

    AvailableColleges: CollegeInfo[];
    AvailableCollegeIDs: number[];
    AvailableReports: ReportInfo[];
    AvailableSchools: SchoolInfo[];
    LastReportOptions: ReportRunOptions;
    PartialViewReady: boolean;
    SmallDisplay: boolean;
    User: UserInfo;

    constructor(
        private appService: ApplicationService,
        private dialogService: DialogService,
        private downloadService: DownloadService,
        private errorService: ErrorService,
        private mediaChange: ObservableMedia,
        private reportsService: ReportsService,
        private splashService: SplashService,
        private userService: UserService
    ) {}

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }

    ngOnInit() {
        const self = this;

        self.AvailableColleges = null;
        self.AvailableCollegeIDs = null;
        self.AvailableReports = null;
        self.AvailableSchools = null;
        self.LastReportOptions = null;
        self.PartialViewReady = false;
        self.User = null;

        self.SmallDisplay = self.mediaChange.isActive('xs') || self.mediaChange.isActive('sm');

        self.mediaChange
            .asObservable()
            .pipe(takeUntil(self._subrelease))
            .subscribe((media) => {
                self.SmallDisplay = media.mqAlias === 'xs' || media.mqAlias === 'sm';
            });

        // Wait for first non-null user object to start component
        self.userService.UserSubject.pipe(
            takeUntil(self._subrelease),
            first((user) => {
                return user && !!user.AuthToken;
            })
        ).subscribe((user) => {
            // Setup the component
            self.User = user;

            // Set up constant monitoring of the user object, to catch AuthToken updates.
            self.userService.UserSubject.pipe(takeUntil(self._subrelease)).subscribe((_user) => {
                self.User = _user;
            });

            // Load all static page info items
            const collegesObs = self.appService.GetAllColleges(self.User.AuthToken);
            const schoolsObs = self.appService.GetAllSchools(self.User.AuthToken);
            const reportsObs = self.reportsService.GetAvailableReports(self.User.AuthToken, self.User.CurrentRole);

            combineLatest(reportsObs, schoolsObs, collegesObs, function (reports, schools, colleges) {
                return {
                    Reports: reports,
                    Schools: schools,
                    Colleges: colleges,
                };
            })
                .pipe(takeUntil(self._subrelease))
                .subscribe(
                    (packet) => {
                        self.AvailableColleges = packet.Colleges;
                        self.AvailableReports = packet.Reports;
                        self.AvailableSchools = packet.Schools;
                        self.AvailableCollegeIDs = packet.Colleges.map(col => col.CollegeID);

                        // Lift block on page
                        self.PartialViewReady = true;
                    },
                    (error) => {
                        self.errorService.HandleServiceError(error);
                    }
                );
        });
    }

    // #region Run Report Method

    RunReport(report: ReportInfo) {
        const self = this;

        if (!report) {
            return;
        }

        let availableColleges: CollegeInfo[] = [];

        // The only people who can set district wide reports are district admins, college advisors, college admins, college support, or developers.
        report.CanSetDistrict =
            [1, 3, 5].includes(report.ReportID) &&
            self.User &&
            (
                self.User.CurrentRole === 'DistrictAdmin' || 
                self.User.CurrentRole === 'Developer' // ||
                // self.User.CurrentRole === 'Advisor' ||
                // self.User.CurrentRole === 'CollegeAdmin' ||
                // self.User.CurrentRole === 'CollegeSupport'
            );

        // The only people who can't set schools are parents, students, or advisors. The rest CAN, but it will be limited
        // to their valid schools.
        report.CanSetSchool =
            self.User &&
            !(
                self.User.CurrentRole === 'Parent' ||
                self.User.CurrentRole === 'Student' 
            );

        let reportObs: Observable<ReportRunOptions>;

        if (report.CanChooseStudent) {
            // Show the student select dialog to get the student ID to use
            reportObs = self.dialogService.OpenStudentSearchDialog({
                TitleText: 'Choose student for report',
                MessageText: 'Please search for and choose a student for this report:',
                ReportInfo: report,
            });
        } else {
            // This requires collecting options. However, if the current role is not a district admin or developer, or any of the college staff roles,
            // then the school should be narrowed down to only valid schools.
            let validSchools: SchoolInfo[] = [];

            if (
                self.User.CurrentRole !== 'DistrictAdmin' && 
                self.User.CurrentRole !== 'Developer' &&
                self.User.CurrentRole !== 'Advisor' &&
                self.User.CurrentRole !== 'CollegeAdmin' &&
                self.User.CurrentRole !== 'CollegeSupport'
            ) {
                // Reduce typically down to a single school - the one this employee is from. However, if the
                // current employee is from Bartow High, Bartow IB, or Summerlin, they can crosscheck between each other.
                // Update on 9/2/16: Asked to removed grouping for Bartow.
                // Update on 11/15/16: Asked to reinstate this. What's old is new again.
                // Update on 11/29/16: Asked to put Haines City in this program.
                // Update on 8/19/20: Have to adjust for Polk Virtual as well, and Traviss Technical, and Ridge Technical, and Lakeland High/Harrison
                const userSchool = self.User.SchoolNumber;
                const isBartow = userSchool === '0901' || userSchool === '0903' || userSchool === '0905';
                const isHaines = userSchool === '1741' || userSchool === '1791';
                const isPolkVirtual = ['7001', '7004', '7006', '7023'].includes(userSchool);
                const isTraviss = ['1591', '1561'].includes(userSchool);
                const isRidge = ['1691', '1661'].includes(userSchool);
                const isLHSHarrison = ['0031', '0033'].includes(userSchool);

                self.AvailableSchools.forEach((sch) => {
                    if (
                        sch.SchoolNumber === userSchool ||
                        (isBartow && (sch.SchoolNumber === '0901' || sch.SchoolNumber === '0903' || sch.SchoolNumber === '0905')) ||
                        (isHaines && (sch.SchoolNumber === '1741' || sch.SchoolNumber === '1791')) ||
                        (isPolkVirtual && ['7001', '7004', '7006', '7023'].includes(sch.SchoolNumber)) ||
                        (isTraviss && ['1591', '1561'].includes(sch.SchoolNumber)) ||
                        (isRidge && ['1691', '1661'].includes(sch.SchoolNumber)) ||
                        (isLHSHarrison && ['0031', '0033'].includes(sch.SchoolNumber))
                    ) {
                        // Add this school
                        validSchools.push(sch);
                    }
                });

                if (self.User.CurrentRole === 'Student') {
                    report.StudentID = self.User.StudentID;
                }

                // Add an "All Schools" option if they have more than one school available.
                if (validSchools.length > 1) {
                    validSchools.unshift({
                        SchoolName: 'All Schools',
                        SchoolNumber: 'All',
                    });
                }
            } else {
                validSchools = self.AvailableSchools.slice();
                
                if (
                    self.User.CurrentRole === 'Advisor' ||
                    self.User.CurrentRole === 'CollegeAdmin' ||
                    self.User.CurrentRole === 'CollegeSupport'
                )  {
                    validSchools.unshift({
                        SchoolName: 'All Schools',
                        SchoolNumber: 'All',
                    });
                }
            }

            if (report.CanSetDistrict === true) {
                validSchools.unshift({
                    SchoolName: 'District Wide',
                    SchoolNumber: '0000',
                });
            }

            if (self.User && self.User.StaffColleges && (
                self.User.CurrentRole === 'Advisor' ||
                self.User.CurrentRole === 'CollegeAdmin' ||
                self.User.CurrentRole === 'CollegeSupport'
            )) {
                self.User.StaffColleges.forEach(x => {
                    const foundCollege = self.AvailableColleges.find(col => col.CollegeID === x.CollegeID)
                    if (foundCollege) 
                        availableColleges.push(foundCollege);
                });
            } else {
                availableColleges = self.AvailableColleges;
            }

            // Pop up the dialog box to collect the options for the report before sending the request to the service.
            reportObs = self.dialogService.OpenReportOptionsDialog({
                TitleText: 'Choose report options',
                MessageText: 'Please set the following options for the report to be generated:',
                AvailableColleges: availableColleges,
                AvailableSchools: validSchools,
                DefaultOptions: self.LastReportOptions,
                ReportInfo: report,
                width: '700px',
                height: '250px',
            });
        }

        // Now that we have report options, run the report
        reportObs.pipe(takeUntil(self._subrelease)).subscribe((options) => {
            if (options) {
                // User has run the report. Use the options provided to make the call to the service and download the report.
                self.LastReportOptions = options;
                const MessageText =  options.ReportSchool.SchoolNumber === '0000' 
                                        ? 'Retrieving report. This could take several minutes...' 
                                        : 'Retrieving report...' ;

                self.splashService.DisplaySplash({ TitleText: 'Please wait', MessageText: MessageText });

                self.downloadService
                    .RetrieveDownload(
                        HttpMethod.GET,
                        'Api/Services/Report/RunReport',
                        {
                            AuthToken: self.User.AuthToken,
                            Role: self.User.CurrentRole,
                            ReportID: options.ReportInfo.ReportID,
                            CollegeID: options.ReportCollege != null ? options.ReportCollege.CollegeID : null,
                            TermWindowID: options.ReportTerm != null ? options.ReportTerm.TermWindowID : null,
                            SchoolNumber: options.ReportSchool != null ? options.ReportSchool.SchoolNumber : null,
                            SchoolName: options.ReportSchool != null ? options.ReportSchool.SchoolName : null,
                            StudentID: options.StudentID,
                            Attachment: true,
                        },
                        report.ReportFileFormat
                    )
                    .subscribe(
                        (result) => {
                            self.splashService.CloseSplash();
                        },
                        (error) => {
                            self.errorService.HandleServiceError(error);
                        }
                    );
            }
        });
    }

    // #endregion
}



// WEBPACK FOOTER //
// ./src/app/reports/reports.component.ts